import React from "react";
import useTableStore, { ALL_TABLE_COLUMNS } from "../../../store/useTabStore";
import { SortConfig } from "../types";

interface TableHeaderProps {
  onSort: (columnName: string) => void;
  sortConfig: SortConfig;
}

const TableHeader: React.FC<TableHeaderProps> = ({ onSort, sortConfig }) => {
  const { visibleColumns } = useTableStore();
  const columnMap = new Map(ALL_TABLE_COLUMNS.map((col) => [col.id, col]));

  const getColumnLabel = (id: string) => {
    return columnMap.get(id)?.label || id;
  };

  const isVisible = (id: string) => visibleColumns.includes(id);

  const formulationIds = [
    "il_molratio",
    "hl_molratio",
    "chl_molratio",
    "peg_molratio",
  ];
  const visibleFormulationCount = formulationIds.filter((id) =>
    isVisible(id)
  ).length;

  const getSortIcon = (columnName: string) => {
    if (sortConfig.column !== columnName) {
      return (
        <svg
          className="w-3 h-3 ms-1.5 text-gray-400"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
        </svg>
      );
    }

    return sortConfig.direction === "ASC" ? (
      <svg
        className="w-3 h-3 ms-1.5 text-blue-500"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Z" />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 ms-1.5 text-blue-500"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M8.574 12.982h6.852a2.075 2.075 0 0 1 1.847 1.086 1.9 1.9 0 0 1-.11 1.986l-3.426 5.05a2.122 2.122 0 0 1-3.472 0L6.837 16.054a1.9 1.9 0 0 1-.11-1.986 2.074 2.074 0 0 1 1.847-1.086Z" />
      </svg>
    );
  };

  const getSortableHeader = (
    title: React.ReactNode,
    columnName: string,
    additionalClasses = ""
  ) => (
    <th
      scope="col"
      className={`px-4 py-3 cursor-pointer ${additionalClasses}`}
      onClick={() => onSort(columnName)}
      rowSpan={2}
    >
      <div className="flex items-center">
        {title}
        {getSortIcon(columnName)}
      </div>
    </th>
  );

  const getSubHeader = (title: string, columnName: string) => (
    <th
      scope="col"
      className="px-4 py-3 cursor-pointer"
      onClick={() => onSort(columnName)}
    >
      <div className="flex items-center">
        {title}
        {getSortIcon(columnName)}
      </div>
    </th>
  );

  return (
    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
      <tr>
        {isVisible("lnp_id") && (
          <th
            rowSpan={2}
            scope="col"
            className="px-4 py-3 font-semibold whitespace-nowrap"
          >
            LNP ID
          </th>
        )}
        {isVisible("library_code") && (
          <th
            rowSpan={2}
            scope="col"
            className="px-4 py-3 font-semibold whitespace-nowrap"
          >
            Library Code
          </th>
        )}
        {isVisible("il") && (
          <th rowSpan={2} scope="col" className="px-4 py-3 font-semibold">
            <div className="whitespace-nowrap">Ionizable Lipid</div>
            <div className="text-xs font-normal">(IL)</div>
          </th>
        )}
        {isVisible("hl") && (
          <th rowSpan={2} scope="col" className="px-4 py-3 font-semibold">
            <div className="whitespace-nowrap">Helper Lipid</div>
            <div className="text-xs font-normal">(HL)</div>
          </th>
        )}
        {isVisible("chl") && (
          <th rowSpan={2} scope="col" className="px-4 py-3 font-semibold">
            <div className="whitespace-nowrap">Cholesterol</div>
            <div className="text-xs font-normal">(CHL)</div>
          </th>
        )}
        {isVisible("peg") && (
          <th rowSpan={2} scope="col" className="px-4 py-3 font-semibold">
            <div className="whitespace-nowrap">PEG Lipid</div>
            <div className="text-xs font-normal">(PEG)</div>
          </th>
        )}

        {visibleFormulationCount > 0 && (
          <th
            colSpan={visibleFormulationCount}
            scope="col"
            className="px-4 py-3 font-semibold text-center"
          >
            <div className="whitespace-nowrap">Formulation (mol ratio %)</div>
          </th>
        )}

        {isVisible("mass_ratio") && (
          <th rowSpan={2} scope="col" className="px-4 py-3 font-semibold">
            IL to Nucleic Acid Mass Ratio
          </th>
        )}
        {isVisible("method") && (
          <th rowSpan={2} scope="col" className="px-4 py-3 font-semibold">
            Experimental Method
          </th>
        )}

        {isVisible("value") &&
          getSortableHeader(
            "Experimental Value",
            "experiment_value",
            "text-center whitespace-nowrap font-semibold"
          )}

        {ALL_TABLE_COLUMNS.filter(
          (col) =>
            ![
              ...formulationIds,
              "lnp_id",
              "library_code",
              "il",
              "hl",
              "chl",
              "peg",
              "mass_ratio",
              "method",
              "value",
              "actions",
            ].includes(col.id) && isVisible(col.id)
        ).map((col) =>
          getSortableHeader(getColumnLabel(col.id), col.id, "font-semibold")
        )}

        {isVisible("actions") && (
          <th
            rowSpan={2}
            scope="col"
            className="relative px-4 py-3 text-sm font-semibold"
          >
            <div className="flex items-center">
              Copy to Clipboard
              <div className="inline-flex items-center ml-1 cursor-help group">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-500"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
            </div>
          </th>
        )}
      </tr>

      <tr className="text-xs text-gray-700 uppercase bg-gray-50">
        {isVisible("il_molratio") && getSubHeader("IL", "il_molratio")}
        {isVisible("hl_molratio") && getSubHeader("HL", "hl_molratio")}
        {isVisible("chl_molratio") && getSubHeader("CHL", "chl_molratio")}
        {isVisible("peg_molratio") && getSubHeader("PEG", "peg_molratio")}
      </tr>
    </thead>
  );
};

export default TableHeader;
